import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Link,
  IconButton,
} from '@mui/material';
import { Google as GoogleIcon, CheckCircle, Error as ErrorIcon, ArrowBack } from '@mui/icons-material';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { applyCommonHeaders } from '../services/httpCommon';
import config from '../config';

// Use auth API (port 8084) for all GSC OAuth endpoints
const AUTH_API_BASE_URL = config.VITE_AUTH_API_BASE_URL?.replace(/\/$/, '') ;
const authApi = axios.create({
  baseURL: AUTH_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Use shared header interceptor (adds Authorization, X-User-Id, X-Tenant-Id, X-Site-Id)
authApi.interceptors.request.use(applyCommonHeaders, (error) => Promise.reject(error));

interface ConnectionStatus {
  connected: boolean;
  siteId: number;
  expiresAt?: string;
}

const steps = ['Connect to Google', 'Authorize Access', 'Complete Setup'];

/**
 * Google Search Console OAuth Connection Wizard
 * 
 * Handles the complete OAuth flow:
 * 1. Initiate authorization (redirect to Google)
 * 2. Handle callback with authorization code
 * 3. Exchange code for tokens
 * 4. Display connection status
 */
export const GscOAuthWizard: React.FC = () => {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = siteIdParam ? parseInt(siteIdParam, 10) : 0;
  
  console.log('GscOAuthWizard - siteIdParam:', siteIdParam, 'siteId:', siteId);
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Validate siteId before making API calls
    if (!siteId || isNaN(siteId)) {
      console.error('Invalid siteId:', siteId, 'from param:', siteIdParam);
      setError('Invalid site ID. Please return to GSC Credentials page and try again.');
      return;
    }

    console.log('Valid siteId, checking connection status for:', siteId);
    // Check existing connection status
    checkConnectionStatus();

    // Handle OAuth callback results (backend redirects here with query params)
    const successParam = searchParams.get('success');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Authorization failed: ${errorParam}`);
      setActiveStep(0);
    } else if (successParam === 'true') {
      // Backend already handled token exchange, just update UI
      setActiveStep(2);
      checkConnectionStatus();
    }
  }, [searchParams]);

  /**
   * Check if site is already connected to GSC
   */
  const checkConnectionStatus = async () => {
    try {
      const response = await authApi.get<ConnectionStatus>(`/oauth/gsc/status?siteId=${siteId}`);
      setConnectionStatus(response.data);
      
      if (response.data.connected) {
        setActiveStep(2); // Skip to final step if already connected
      }
    } catch (err) {
      console.error('Failed to check connection status:', err);
    }
  };

  /**
   * Step 1: Initiate OAuth flow
   * Redirects user to Google authorization page
   */
  const handleConnect = async () => {
    if (!siteId || isNaN(siteId)) {
      setError('Invalid site ID. Please return to GSC Credentials page and try again.');
      return;
    }

    setLoading(true);
    setError(null);

    // Use authentication service endpoint as OAuth redirect URI
    // Pass siteId as a query parameter to the authorization endpoint
    // Backend will embed it in the state parameter for OAuth flow
    const authBaseUrl = config.VITE_AUTH_API_BASE_URL?.replace('/api/v1', '') ;
    const redirectUri = `${authBaseUrl}/api/v1/oauth/gsc/callback`;
    console.log('handleConnect - siteId:', siteId, 'redirectUri:', redirectUri);

    try {
      const response = await authApi.get<any>(
        '/oauth/gsc/authorize',
        { params: { redirectUri, siteId } }
      );

      console.log('API response:', response);
      console.log('Full response.data:', response.data);

      // Backend returns nested structure: { data: { authorizationUrl: "..." } }
      const authUrl = response.data.data?.authorizationUrl || response.data.authorizationUrl;
      console.log('Authorization URL:', authUrl);

      // Redirect to Google authorization page
      if (authUrl) {
        console.log('Redirecting to:', authUrl);
        window.location.href = authUrl;
      } else {
        setError('No authorization URL received from server');
        setLoading(false);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      
      // If backend returns 500 or no specific error, it means the OAuth controller isn't implemented
      if (err.response?.status === 500 || !errorMsg) {
        setError('Google Search Console OAuth integration is not yet implemented on the backend. This feature requires the GSC OAuth controller to be developed.');
      } else {
        setError(errorMsg);
      }
      setLoading(false);
    }
  };

  /**
   * Step 2: Handle OAuth callback
   * Exchange authorization code for tokens
   */
  const handleOAuthCallback = async (code: string) => {
    setLoading(true);
    setError(null);
    setActiveStep(1);

    // Use authentication service endpoint as OAuth redirect URI (must match authorization request)
    const authBaseUrl = config.VITE_AUTH_API_BASE_URL?.replace('/api/v1', '');
    const redirectUri = `${authBaseUrl}/api/v1/oauth/gsc/callback`;

    try {
      await authApi.post('/oauth/gsc/callback', {
        siteId,
        code,
        redirectUri,
      });

      setActiveStep(2);
      await checkConnectionStatus();
      
      // Clean up URL
      navigate('/settings/integrations', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to exchange authorization code');
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Disconnect from GSC
   */
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Search Console?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authApi.delete(`/oauth/gsc/disconnect?siteId=${siteId}`);
      setConnectionStatus({ connected: false, siteId });
      setActiveStep(0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/webtools/config')}
            aria-label="back to site configuration"
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h2">
            Connect Google Search Console
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {/* Step 0: Initial Connection */}
        {activeStep === 0 && !connectionStatus?.connected && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <GoogleIcon sx={{ fontSize: 64, color: '#4285F4', mb: 2 }} />
            <Typography variant="body1" paragraph>
              Connect your site to Google Search Console to start collecting search performance data.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You'll be redirected to Google to authorize access. We only request read-only access
              to your Search Console data.
            </Typography>
            <Button
              type="button"
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={(e) => {
                e.preventDefault();
                console.log('Button clicked, calling handleConnect with siteId:', siteId);
                handleConnect();
              }}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Connect with Google'}
            </Button>
          </Box>
        )}

        {/* Step 1: Processing Authorization */}
        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={64} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Processing authorization...
            </Typography>
          </Box>
        )}

        {/* Step 2: Connected Successfully */}
        {activeStep === 2 && connectionStatus?.connected && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Successfully Connected!
            </Typography>
            <Typography variant="body1" paragraph>
              Your site is now connected to Google Search Console.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We'll automatically start collecting search performance data. You can view your
              metrics in the dashboard.
            </Typography>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={loading}
              >
                Disconnect
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </Box>
          </Box>
        )}

        {/* Privacy & Security Info */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Privacy & Security:</strong> We use industry-standard encryption to protect your credentials.
            Your refresh tokens are encrypted with AES-256-GCM. We only request read-only access to your
            Search Console data and never make changes to your Google account.{' '}
            <Link href="/privacy" target="_blank">Learn more</Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GscOAuthWizard;
